import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from "../includes/sidebar";
import { Topbar } from "../includes/topbar";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "../../components/ui/dropdown-menu";
import { MoreHorizontal, Search, Eye, Upload, Download, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpRight, ArrowDownLeft, RefreshCcw, Plus, Loader2 } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { transactionsApi, Transaction, TransactionStatus, GroupedTransactionItem } from "../../api/transactions.api";
import { comptesApi, Compte } from "../../api/clients.api";
import { useToast } from "../../hooks/use-toast";
import { useAuth } from "../../context/AuthContext";
import { XCircle } from "lucide-react";

export default function TransactionsPage() {
    const { t } = useTranslation(['transactions', 'common', 'dashboard']);
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchTerm, setSearchTerm] = React.useState("");
    const [currentPage, setCurrentPage] = React.useState(1);
    const [itemsPerPage, setItemsPerPage] = React.useState(5);
    const [statusFilter, setStatusFilter] = React.useState<string>("Tous");
    const [selectedAccountId, setSelectedAccountId] = React.useState<string>("all");
    const [debouncedSearch, setDebouncedSearch] = React.useState("");

    // Debounce search term
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when search or filters change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, statusFilter, selectedAccountId]);

    // API state
    const [transactions, setTransactions] = React.useState<GroupedTransactionItem[]>([]);
    const [accounts, setAccounts] = React.useState<Compte[]>([]);
    const [totalCount, setTotalCount] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(true);
    const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set());
    const [selectedTransaction, setSelectedTransaction] = React.useState<GroupedTransactionItem | null>(null);
    const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
    
    // Cancellation state
    const [isCancelDialogOpen, setIsCancelDialogOpen] = React.useState(false);
    const [transactionToCancel, setTransactionToCancel] = React.useState<GroupedTransactionItem | null>(null);
    const [cancelReason, setCancelReason] = React.useState("");
    const [isCancelling, setIsCancelling] = React.useState(false);

    const { toast } = useToast();

    // Fetch user accounts
    React.useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const response = await comptesApi.getMyAccounts();
                if (response && response.results) {
                    setAccounts(response.results);
                }
            } catch (error) {
                console.error("Error fetching accounts:", error);
            }
        };
        fetchAccounts();
    }, []);

    // Toggle group expansion
    const toggleGroupExpansion = (groupKey: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupKey)) {
                next.delete(groupKey);
            } else {
                next.add(groupKey);
            }
            return next;
        });
    };

    // Open transaction details
    const openDetails = (trx: GroupedTransactionItem) => {
        setSelectedTransaction(trx);
        setIsDetailsOpen(true);
    };

    // Fetch transactions
    const fetchTransactions = React.useCallback(async () => {
        setIsLoading(true);
        try {
            const statusMap: Record<string, TransactionStatus | undefined> = {
                'all': undefined,
                'reussi': 'reussi',
                'en_cours': 'en_cours',
                'echoue': 'echoue',
                'annule': 'annule',
            };

            // Default date window: last 30 days — prevents full-table scan on admin view
            const defaultDateDebut = new Date();
            defaultDateDebut.setDate(defaultDateDebut.getDate() - 30);
            const defaultDateDebutStr = defaultDateDebut.toISOString().split('T')[0];

            const filters: any = {
                page: currentPage,
                page_size: itemsPerPage,
                status: statusMap[statusFilter],
                group_sol: true, // Enable SOL grouping
                date_debut: defaultDateDebutStr, // Limit query to last 30 days by default
            };

            if (selectedAccountId && selectedAccountId !== "all") {
                filters.compte_id = selectedAccountId;
            }

            if (debouncedSearch) {
                filters.search = debouncedSearch;
            }

            const response = await transactionsApi.list(filters);

            setTransactions(response.results as unknown as GroupedTransactionItem[]);
            setTotalCount(response.count);
        } catch (error: any) {
            console.error('Error fetching transactions:', error);
            toast({ title: "Erreur", description: "Echec chargement des transactions", type: "error" });
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, itemsPerPage, statusFilter, selectedAccountId, debouncedSearch]);

    React.useEffect(() => {
        fetchTransactions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchTransactions]);

    // Handle cancellation
    const handleCancelClick = (trx: GroupedTransactionItem) => {
        setTransactionToCancel(trx);
        setCancelReason("");
        setIsCancelDialogOpen(true);
    };

    const handleCancelConfirm = async () => {
        if (!transactionToCancel?.id) return;
        
        setIsCancelling(true);
        try {
            await transactionsApi.cancel({
                transaction_id: transactionToCancel.id,
                reason: cancelReason || "Annulation par administrateur"
            });
            
            toast({
                title: "Tranzaksyon anile",
                description: "Tranzaksyon an anile ak siksè, solde a ajiste.",
            });
            
            setIsCancelDialogOpen(false);
            fetchTransactions();
        } catch (error: any) {
            console.error("Cancellation error:", error);
            toast({
                title: "Erè",
                description: error.response?.data?.error || "Echèk pou anile tranzaksyon an.",
                type: "error",
            });
        } finally {
            setIsCancelling(false);
        }
    };

    // Use transactions directly (already filtered by server)
    const filteredTransactions = transactions;

    const totalPages = Math.ceil(totalCount / itemsPerPage);

    // Helper: Map backend type to display label
    const getTypeLabel = (type: string): string => {
        const labels: Record<string, string> = {
            'depo_bs': 'Depot BS',
            'retrait_bs': 'Retrait BS',
            'depo_sol': 'Depot SOL',
            'depo_solide': 'Depot Solidè',
            'retrait_sol': 'Retrait SOL',
            'transfer': 'Transfert',
            'paiement': 'Paiement',
        };
        return labels[type] || type;
    };

    // Helper: Map backend status to display
    const getStatusBadge = (status: TransactionStatus) => {
        const config: Record<TransactionStatus, { label: string; className: string }> = {
            'reussi': { label: 'Succès', className: 'text-green-600 border-green-600 bg-green-50' },
            'en_cours': { label: 'En attente', className: 'text-yellow-600 border-yellow-600 bg-yellow-50' },
            'echoue': { label: 'Échoué', className: 'text-red-600 border-red-600 bg-red-50' },
            'annule': { label: 'Annulé', className: 'text-gray-600 border-gray-600 bg-gray-50' },
        };
        const { label, className } = config[status] || { label: status, className: '' };
        return <Badge variant="outline" className={className}>{label}</Badge>;
    };

    // Helper: Format amount
    const formatAmount = (montant: string): string => {
        const num = parseFloat(montant);
        return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(num) + ' HTG';
    };

    // Helper: Format date
    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        return date.toLocaleString('fr-FR', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        });
    };



    return (
        <>
            <div className="flex h-screen overflow-hidden">
                <Sidebar />
                <div className="flex-1 flex flex-col overflow-hidden">
                    <Topbar />
                    <div className="flex-1 overflow-y-auto space-y-4 p-4 sm:p-8 pt-6">
                        <div className="flex items-center justify-between space-y-2">
                            <Breadcrumb>
                                <BreadcrumbList>
                                    <BreadcrumbItem>
                                        <BreadcrumbLink className="capitalize">{t('transactions')}</BreadcrumbLink>
                                    </BreadcrumbItem>
                                    <BreadcrumbSeparator />
                                    <BreadcrumbItem>
                                        <BreadcrumbPage className="capitalize">{t('history')}</BreadcrumbPage>
                                    </BreadcrumbItem>
                                </BreadcrumbList>
                            </Breadcrumb>
                        </div>
                        <Card>
                            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 space-y-0 pb-2">
                                <div className="space-y-1">
                                    <CardTitle>{t('transactions')}</CardTitle>
                                    <CardDescription>
                                        {t('transactionsOfDay')}
                                    </CardDescription>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Button size="sm" variant="outline" className="flex-1 sm:flex-none" onClick={fetchTransactions}>
                                        <RefreshCcw className="mr-2 h-4 w-4" /> {t('refresh', { ns: 'common' })}
                                    </Button>
                                    <Button size="sm" className="flex-1 sm:flex-none" onClick={() => navigate('/transactions/new')}>
                                        <Plus className="mr-2 h-4 w-4" /> {t('newTransaction.title')}
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col md:flex-row items-stretch md:items-center py-4 gap-2 md:gap-4">
                                    <div className="relative w-full md:max-w-sm">
                                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            placeholder={t('payments.searchPlaceholder')}
                                            className="pl-8 w-full"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                                        <Select value={selectedAccountId} onValueChange={(val) => { setSelectedAccountId(val); setCurrentPage(1); }}>
                                            <SelectTrigger className="w-full md:w-[200px]">
                                                <SelectValue placeholder={t('account')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">{t('all')}</SelectItem>
                                                {accounts.map(account => (
                                                    <SelectItem key={account.id} value={account.id}>
                                                        {account.code} - {account.type}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setCurrentPage(1); }}>
                                            <SelectTrigger className="w-full md:w-[180px]">
                                                <SelectValue placeholder={t('statusLabel')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">{t('status.all')}</SelectItem>
                                                <SelectItem value="reussi">{t('status.reussi')}</SelectItem>
                                                <SelectItem value="en_cours">{t('status.en_cours')}</SelectItem>
                                                <SelectItem value="echoue">{t('status.echoue')}</SelectItem>
                                                <SelectItem value="annule">{t('status.annule')}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button size="sm" variant="outline" className="w-full md:w-auto">
                                        <Upload className="mr-2 h-4 w-4" /> {t('export', { ns: 'common' })}
                                    </Button>
                                </div>
                                <div className="rounded-md border overflow-x-auto w-full">
                                    <Table className="min-w-[800px] md:min-w-full">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>ID</TableHead>
                                                <TableHead>{t('type')}</TableHead>
                                                <TableHead>{t('client')}</TableHead>
                                                <TableHead>{t('amount')}</TableHead>
                                                <TableHead>{t('date')}</TableHead>
                                                <TableHead>{t('statusLabel')}</TableHead>
                                                <TableHead className="text-right">{t('payments.table.action')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {isLoading ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} className="h-24 text-center">
                                                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                                                    </TableCell>
                                                </TableRow>
                                            ) : filteredTransactions.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                                                        {t('noTransactions')}
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                filteredTransactions.map((trx, index) => (
                                                    trx.is_group ? (
                                                        // Grouped SOL transactions
                                                        <React.Fragment key={trx.group_key || index}>
                                                            <TableRow
                                                                className="bg-blue-50/50 dark:bg-blue-900/20 cursor-pointer hover:bg-blue-100/50"
                                                                onClick={() => toggleGroupExpansion(trx.group_key!)}
                                                            >
                                                                <TableCell className="font-mono text-xs font-medium">
                                                                    <Badge variant="secondary" className="bg-blue-500/20 text-blue-600 border-0">
                                                                        {trx.count} tranzaksyon
                                                                    </Badge>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <div className="flex items-center gap-2">
                                                                        <ArrowDownLeft className="h-4 w-4 text-green-500" />
                                                                        <span className="font-medium">Dépôt SOL (Gwoup)</span>
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <div>
                                                                        <p className="font-medium">{trx.client_nom || 'N/A'}</p>
                                                                        <Badge variant="secondary" className="text-[10px] font-normal mt-0.5">
                                                                            {trx.compte_code || 'N/A'}
                                                                        </Badge>
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell className="font-bold text-green-600">
                                                                    {formatAmount(trx.total_montant!)}
                                                                </TableCell>
                                                                <TableCell className="text-muted-foreground text-sm">
                                                                    {formatDate(trx.latest_created_at!)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Badge className="bg-blue-500/20 text-blue-600 border-0">
                                                                        {t('grouped')}
                                                                    </Badge>
                                                                </TableCell>
                                                                <TableCell className="text-right">
                                                                    <div className="flex items-center justify-end gap-2">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-8 px-2 text-xs"
                                                                            onClick={() => toggleGroupExpansion(trx.group_key!)}
                                                                        >
                                                                            {expandedGroups.has(trx.group_key!) ? t('newTransaction.lastTransactions.close') : t('newTransaction.lastTransactions.showDetails')}
                                                                        </Button>

                                                                        <DropdownMenu>
                                                                            <DropdownMenuTrigger asChild>
                                                                                <Button variant="ghost" className="h-8 w-8 p-0">
                                                                                    <MoreHorizontal className="h-4 w-4" />
                                                                                </Button>
                                                                            </DropdownMenuTrigger>
                                                                            <DropdownMenuContent align="end">
                                                                                <DropdownMenuLabel>{t('payments.table.action')}</DropdownMenuLabel>
                                                                                <DropdownMenuSeparator className='bg-primary-500' />
                                                                                <DropdownMenuItem onClick={() => openDetails(trx)}>
                                                                                    <Eye className="mr-2 h-4 w-4" />
                                                                                    {t('detailsGroup')}
                                                                                </DropdownMenuItem>
                                                                                <DropdownMenuItem>
                                                                                    <Download className="mr-2 h-4 w-4" />
                                                                                    {t('receipt.pdf')}
                                                                                </DropdownMenuItem>
                                                                            </DropdownMenuContent>
                                                                        </DropdownMenu>
                                                                    </div>
                                                                </TableCell>
                                                            </TableRow>
                                                            {/* Expanded individual transactions */}
                                                            {expandedGroups.has(trx.group_key!) && trx.transactions?.map((subTx) => (
                                                                <TableRow key={subTx.id} className="bg-blue-50/30 dark:bg-blue-900/10 border-l-2 border-blue-500">
                                                                    <TableCell className="font-mono text-xs text-muted-foreground pl-6">
                                                                        ↳ {subTx.id.slice(0, 8)}...
                                                                    </TableCell>
                                                                    <TableCell className="text-muted-foreground">
                                                                        <div className="flex items-center gap-2 pl-2">
                                                                            <ArrowDownLeft className="h-3 w-3 text-green-400" />
                                                                            <span>{t('types.depo_sol')}</span>
                                                                        </div>
                                                                    </TableCell>
                                                                    <TableCell className="text-muted-foreground text-xs">
                                                                        {subTx.date_sol || '-'}
                                                                    </TableCell>
                                                                    <TableCell className="font-medium">{formatAmount(subTx.montant)}</TableCell>
                                                                    <TableCell className="text-muted-foreground text-xs">
                                                                        {formatDate(subTx.created_at)}
                                                                    </TableCell>
                                                                    <TableCell>{getStatusBadge(subTx.status)}</TableCell>
                                                                    <TableCell className="text-right">
                                                                        <DropdownMenu>
                                                                            <DropdownMenuTrigger asChild>
                                                                                <Button variant="ghost" className="h-8 w-8 p-0">
                                                                                    <MoreHorizontal className="h-3 w-3" />
                                                                                </Button>
                                                                            </DropdownMenuTrigger>
                                                                            <DropdownMenuContent align="end">
                                                                                 <DropdownMenuLabel className="text-xs">Sub-Transaction</DropdownMenuLabel>
                                                                                <DropdownMenuSeparator />
                                                                                <DropdownMenuItem onClick={() => openDetails({
                                                                                    ...subTx,
                                                                                    client_nom: trx.client_nom,
                                                                                    compte_code: trx.compte_code,
                                                                                    type: trx.type,
                                                                                    is_group: false
                                                                                })}>
                                                                                    <Eye className="mr-2 h-4 w-4" />
                                                                                    {t('details')}
                                                                                </DropdownMenuItem>
                                                                                <DropdownMenuItem>
                                                                                    <Download className="mr-2 h-4 w-4" />
                                                                                    {t('receipt.pdf')}
                                                                                </DropdownMenuItem>
                                                                                {(user?.role === 'Admin' || user?.role === 'Superadmin') && subTx.status === 'reussi' && (
                                                                                    <>
                                                                                        <DropdownMenuSeparator />
                                                                                        <DropdownMenuItem 
                                                                                            className="text-red-600 focus:text-red-600"
                                                                                            onClick={() => handleCancelClick({
                                                                                                ...subTx,
                                                                                                client_nom: trx.client_nom,
                                                                                                compte_code: trx.compte_code,
                                                                                                type: trx.type,
                                                                                                is_group: false
                                                                                            })}
                                                                                        >
                                                                                            <XCircle className="mr-2 h-4 w-4" />
                                                                                            Annuler
                                                                                        </DropdownMenuItem>
                                                                                    </>
                                                                                 )}
                                                                            </DropdownMenuContent>
                                                                        </DropdownMenu>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </React.Fragment>
                                                    ) : (
                                                        // Single transaction
                                                        <TableRow key={trx.id}>
                                                            <TableCell className="font-mono text-xs font-medium">{trx.id!.slice(0, 8)}...</TableCell>
                                                            <TableCell>
                                                                <div className="flex items-center gap-2">
                                                                    {trx.type.includes('depo') && <ArrowDownLeft className="h-4 w-4 text-green-500" />}
                                                                    {trx.type.includes('retrait') && <ArrowUpRight className="h-4 w-4 text-red-500" />}
                                                                    {trx.type === 'transfer' && <RefreshCcw className="h-4 w-4 text-blue-500" />}
                                                                    <span>{getTypeLabel(trx.type)}</span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                <div>
                                                                    <p className="font-medium">{trx.client_nom || 'N/A'}</p>
                                                                    <Badge variant="secondary" className="text-[10px] font-normal mt-0.5">
                                                                        {trx.compte_code || 'N/A'}
                                                                    </Badge>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="font-bold">{formatAmount(trx.montant!)}</TableCell>
                                                            <TableCell className="text-muted-foreground text-sm">{formatDate(trx.created_at!)}</TableCell>
                                                            <TableCell>
                                                                {getStatusBadge(trx.status!)}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild>
                                                                        <Button variant="ghost" className="h-8 w-8 p-0">
                                                                            <span className="sr-only">Open menu</span>
                                                                            <MoreHorizontal className="h-4 w-4" />
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent align="end">
                                                                        <DropdownMenuLabel>{t('payments.table.action')}</DropdownMenuLabel>
                                                                        <DropdownMenuSeparator className='bg-primary-500' />
                                                                        <DropdownMenuItem onClick={() => openDetails(trx)}>
                                                                            <Eye className="mr-2 h-4 w-4" />
                                                                            {t('details')}
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem>
                                                                            <Download className="mr-2 h-4 w-4" />
                                                                            {t('receipt.pdf')}
                                                                        </DropdownMenuItem>
                                                                        {(user?.role === 'Admin' || user?.role === 'Superadmin') && trx.status === 'reussi' && !trx.is_group && (
                                                                            <>
                                                                                <DropdownMenuSeparator />
                                                                                <DropdownMenuItem 
                                                                                    className="text-red-600 focus:text-red-600"
                                                                                    onClick={() => handleCancelClick(trx)}
                                                                                >
                                                                                    <XCircle className="mr-2 h-4 w-4" />
                                                                                    Annuler
                                                                                </DropdownMenuItem>
                                                                            </>
                                                                        )}
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
                                    <div className="flex items-center space-x-2 self-start sm:self-center">
                                        <p className="text-sm font-medium">Lignes par page:</p>
                                        <Select
                                            value={`${itemsPerPage}`}
                                            onValueChange={(value) => {
                                                setItemsPerPage(Number(value));
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <SelectTrigger className="h-8 w-[70px]">
                                                <SelectValue placeholder={itemsPerPage} />
                                            </SelectTrigger>
                                            <SelectContent side="top">
                                                {[5, 10, 20, 50].map((pageSize) => (
                                                    <SelectItem key={pageSize} value={`${pageSize}`}>
                                                        {pageSize}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
                                        <div className="text-sm font-medium">
                                            {totalCount > 0 ? (
                                                `${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, totalCount)} sur ${totalCount}`
                                            ) : (
                                                "0-0 sur 0"
                                            )}
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Button
                                                variant="outline"
                                                className="hidden h-8 w-8 p-0 lg:flex"
                                                onClick={() => setCurrentPage(1)}
                                                disabled={currentPage === 1}
                                            >
                                                <span className="sr-only">Go to first page</span>
                                                <ChevronsLeft className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="h-8 w-8 p-0"
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                            >
                                                <span className="sr-only">Go to previous page</span>
                                                <ChevronLeft className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="h-8 w-8 p-0"
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={currentPage === totalPages}
                                            >
                                                <span className="sr-only">Go to next page</span>
                                                <ChevronRight className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="hidden h-8 w-8 p-0 lg:flex"
                                                onClick={() => setCurrentPage(totalPages)}
                                                disabled={currentPage === totalPages}
                                            >
                                                <span className="sr-only">Go to last page</span>
                                                <ChevronsRight className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Transaction Details Modal */}
            <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {selectedTransaction?.type?.includes('retrait') && <ArrowUpRight className="h-5 w-5" />}
                            {selectedTransaction?.type?.includes('depo') && <ArrowDownLeft className="h-5 w-5" />}
                            {selectedTransaction?.type === 'transfer' && <RefreshCcw className="h-5 w-5" />}
                            {getTypeLabel(selectedTransaction?.type || '')}
                        </DialogTitle>
                        <DialogDescription>
                            {selectedTransaction?.is_group
                                ? `${selectedTransaction?.count} ${t('newTransaction.lastTransactions.count', { count: selectedTransaction?.count })}`
                                : formatDate(selectedTransaction?.created_at || '')}
                        </DialogDescription>
                    </DialogHeader>

                    {selectedTransaction && (
                        <div className="space-y-5 mt-2">
                            {/* Amount & Status */}
                            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                                <div>
                                    <p className="text-xs text-muted-foreground uppercase mb-1">{t('amount')}</p>
                                    <p className="text-2xl font-bold">
                                        {selectedTransaction.type?.includes('retrait') ? '-' : '+'}
                                        {formatAmount(selectedTransaction.is_group
                                            ? selectedTransaction.total_montant || '0'
                                            : selectedTransaction.montant || '0')}
                                    </p>
                                </div>
                                {!selectedTransaction.is_group && selectedTransaction.status && (
                                    getStatusBadge(selectedTransaction.status)
                                )}
                                {selectedTransaction.is_group && (
                                    <Badge variant="secondary">{selectedTransaction.count} {t('transactions')}</Badge>
                                )}
                            </div>

                            {/* Client Info */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between py-2 border-b">
                                    <span className="text-sm text-muted-foreground">{t('client')}</span>
                                    <span className="text-sm font-medium">{selectedTransaction.client_nom || 'N/A'}</span>
                                </div>
                                <div className="flex items-center justify-between py-2 border-b">
                                    <span className="text-sm text-muted-foreground">{t('account')}</span>
                                    <span className="text-sm font-mono">{selectedTransaction.compte_code || 'N/A'}</span>
                                </div>
                                <div className="flex items-center justify-between py-2 border-b">
                                    <span className="text-sm text-muted-foreground">{t('date')}</span>
                                    <span className="text-sm">{formatDate(selectedTransaction.is_group
                                        ? selectedTransaction.latest_created_at || ''
                                        : selectedTransaction.created_at || '')}</span>
                                </div>
                                {selectedTransaction.date_sol && (
                                    <div className="flex items-center justify-between py-2 border-b">
                                        <span className="text-sm text-muted-foreground">Dat SOL</span>
                                        <span className="text-sm">{selectedTransaction.date_sol}</span>
                                    </div>
                                )}
                            </div>

                            {/* Description & Fees */}
                            {selectedTransaction.description && (
                                <div className="space-y-2">
                                    <p className="text-xs text-muted-foreground uppercase">{t('detailsOperation')}</p>
                                    {(() => {
                                        const desc = selectedTransaction.description || '';
                                        const feeMatch = desc.match(/Frais:\s*([\d.,]+)\s*HTG/i);
                                        const actionMatch = desc.match(/^([^|]+)/);

                                        return (
                                            <div className="text-sm space-y-2">
                                                {actionMatch && <p>{actionMatch[1].trim()}</p>}
                                                {feeMatch && (
                                                    <div className="flex items-center justify-between p-2 bg-muted rounded">
                                                        <span className="text-muted-foreground">Frè sèvis</span>
                                                        <span className="font-medium">{feeMatch[1]} HTG</span>
                                                    </div>
                                                )}
                                                {!feeMatch && !actionMatch && <p>{desc}</p>}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* Grouped Transactions */}
                            {selectedTransaction.is_group && selectedTransaction.transactions && (
                                <div className="space-y-2">
                                    <p className="text-xs text-muted-foreground uppercase">Tranzaksyon nan gwoup la</p>
                                    <div className="space-y-1 max-h-40 overflow-y-auto">
                                        {selectedTransaction.transactions.map((tx, idx) => (
                                            <div key={tx.id} className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-muted-foreground">{idx + 1}.</span>
                                                    <span className="text-muted-foreground">
                                                        {tx.date_sol || formatDate(tx.created_at).split(' ')[0]}
                                                    </span>
                                                </div>
                                                <span className="font-medium">{formatAmount(tx.montant)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Transaction ID */}
                            <div className="pt-2">
                                <p className="text-xs text-muted-foreground">ID:
                                    <span className="font-mono ml-1">{selectedTransaction.id || selectedTransaction.group_key}</span>
                                </p>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Cancellation Confirmation Dialog */}
            <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <XCircle className="h-5 w-5" />
                            Annulasyon Tranzaksyon
                        </DialogTitle>
                        <DialogDescription>
                            Èske ou sèten ou vle anile tranzaksyon sa a? 
                            Aksyon sa a pral retire montant an sou solde kliyan an epi li pèmanan.
                        </DialogDescription>
                    </DialogHeader>

                    {transactionToCancel && (
                        <div className="space-y-4 py-2">
                            <div className="p-3 bg-red-50 dark:bg-red-900/10 rounded border border-red-100 dark:border-red-900/20 text-sm space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">ID:</span>
                                    <span className="font-mono">{transactionToCancel.id?.slice(0, 12)}...</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Tip:</span>
                                    <span className="font-medium">{getTypeLabel(transactionToCancel.type)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Montant:</span>
                                    <span className="font-bold text-red-600">{formatAmount(transactionToCancel.montant || '0')}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Kliyan:</span>
                                    <span className="font-medium">{transactionToCancel.client_nom}</span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-medium uppercase text-muted-foreground">
                                    Rizon annulasyon an (opsyonèl)
                                </label>
                                <Input 
                                    placeholder="Ekri poukisa w'ap anile..." 
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <Button variant="outline" onClick={() => setIsCancelDialogOpen(false)} disabled={isCancelling}>
                                    Non, tounen
                                </Button>
                                <Button 
                                    variant="destructive" 
                                    onClick={handleCancelConfirm} 
                                    disabled={isCancelling}
                                >
                                    {isCancelling ? (
                                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Map anile...</>
                                    ) : (
                                        "Wi, Anile'l"
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

